import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { ArrowLeft, BookMarked, Bot, PanelLeftClose, PanelLeftOpen, Search, Table2, X } from "lucide-react";
import { Alert, Button, EmptyState, ErrorState, Input, Select } from "@/components/ui";
import { useContextApi, isAbortError, ContextApiError, type Concept, type TableCard, type TableSummary, type Version } from "@/lib/contextApi";
import { cn } from "@/lib/utils";
import { CardHeaderBlock, CardSectionBody, cardSections, shortFqn, type CardSection } from "./ContextCard";
import { CopyButton, InlineText, KindBadge, kindLabel, useDebounced, useErrorHandler } from "./shared";

export type CatalogTab = "tabellen" | "begrippen";
export type BrowserSelection = { type: "table"; fqn: string } | { type: "concept"; name: string } | null;

function useTables(version: string | undefined, q: string, domain: string) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [data, setData] = useState<TableSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listTables({ q, domain, version }, ac.signal)
      .then((d) => setData(Array.isArray(d) ? d : []))
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(handleError(e));
        setData(null);
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, handleError, version, q, domain, retry]);
  return { data, loading, error, retry: () => setRetry((r) => r + 1) };
}

function useConcepts(version: string | undefined, enabled: boolean) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [data, setData] = useState<Concept[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    api
      .listConcepts(version, ac.signal)
      .then((d) => setData(Array.isArray(d) ? d : []))
      .catch((e) => {
        if (isAbortError(e)) return;
        setError(handleError(e));
        setData(null);
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, handleError, version, enabled, retry]);
  return { data, loading, error, retry: () => setRetry((r) => r + 1) };
}

/** Highlight the first case-insensitive occurrence of `q` in `text`. */
function Hl({ text, q }: { text: string; q: string }) {
  if (!q) return <>{text}</>;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-sm bg-sl-yellow/50 px-px text-inherit">{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
}

function ListSkeleton() {
  return (
    <div className="space-y-px" aria-hidden>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="px-4 py-3">
          <div className="h-3.5 w-3/5 animate-pulse rounded bg-muted" />
          <div className="mt-2 h-3 w-4/5 animate-pulse rounded bg-muted/70" />
        </div>
      ))}
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="px-5 py-6 lg:px-8" aria-label="Tabelkaart laden" role="status">
      <div className="h-3 w-40 animate-pulse rounded bg-muted" />
      <div className="mt-3 h-7 w-96 max-w-full animate-pulse rounded bg-muted" />
      <div className="mt-4 h-4 w-full max-w-3xl animate-pulse rounded bg-muted/70" />
      <div className="mt-2 h-4 w-2/3 max-w-2xl animate-pulse rounded bg-muted/70" />
      <div className="mt-5 h-16 w-full animate-pulse rounded-lg bg-muted/60" />
      <div className="mt-6 h-64 w-full animate-pulse rounded-lg bg-muted/40" />
    </div>
  );
}

/** Arrow-key navigation between the list's buttons. */
function onListKey(e: KeyboardEvent<HTMLUListElement>) {
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  const btns = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>("button[data-item]"));
  const i = btns.indexOf(document.activeElement as HTMLButtonElement);
  if (i < 0) return;
  e.preventDefault();
  const next = btns[Math.min(btns.length - 1, Math.max(0, i + (e.key === "ArrowDown" ? 1 : -1)))];
  next?.focus();
  next?.click();
}

function TableDetail({
  fqn,
  version,
  section,
  onSection,
  onOpenTable,
  onAsk,
  onBack,
  onSearch,
}: {
  onSearch: (q: string) => void;
  fqn: string;
  version?: string;
  section: CardSection;
  onSection: (s: CardSection) => void;
  onOpenTable: (fqn: string) => void;
  onAsk: (fqn: string) => void;
  onBack: () => void;
}) {
  const api = useContextApi();
  const handleError = useErrorHandler();
  const [card, setCard] = useState<TableCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [retry, setRetry] = useState(0);
  const [stuck, setStuck] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setNotFound(false);
    setCard(null);
    api
      .getTable(fqn, version, ac.signal)
      .then((c) => {
        setCard(c && typeof c === "object" ? c : {});
        rememberRecent(fqn);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        if (e instanceof ContextApiError && e.status === 404) setNotFound(true);
        setError(handleError(e));
      })
      .finally(() => !ac.signal.aborted && setLoading(false));
    return () => ac.abort();
  }, [api, handleError, fqn, version, retry]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setStuck(!e.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [card]);

  const sections = useMemo(() => (card ? cardSections(card) : []), [card]);
  const active: CardSection = sections.some((s) => s.id === section) ? section : "overzicht";
  const name = fqn.split(".").pop() ?? fqn;

  const back = (
    <button type="button" onClick={onBack} className="inline-flex h-10 items-center gap-1.5 px-4 text-sm font-medium text-muted-foreground hover:text-foreground lg:hidden">
      <ArrowLeft className="h-4 w-4" />
      Alle tabellen
    </button>
  );

  if (loading)
    return (
      <>
        {back}
        <DetailSkeleton />
      </>
    );
  if (notFound)
    return (
      <div className="p-5 lg:p-8">
        {back}
        <div className="mx-auto max-w-lg py-10 text-center">
          <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Table2 className="h-5 w-5" />
          </div>
          <h2 className="text-lg font-semibold">Deze tabel staat niet in {version ? "deze versie" : "de live versie"}</h2>
          <p className="mt-1 text-[15px] text-muted-foreground">
            Er is geen kaart voor <span className="font-mono text-foreground">{fqn}</span>. Misschien is de naam veranderd, of bestaat de tabel alleen in een andere versie.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button variant="primary" size="sm" onClick={() => onSearch(name)}>
              <Search className="h-4 w-4" />
              Zoek naar “{name}”
            </Button>
            <Button variant="outline" size="sm" onClick={onBack}>
              Naar de catalogus
            </Button>
          </div>
          {error && (
            <details className="mt-6 text-left text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">Technische details</summary>
              <p className="mt-1 break-all font-mono">{error}</p>
            </details>
          )}
        </div>
      </div>
    );
  if (error)
    return (
      <div className="p-5 lg:p-8">
        {back}
        <ErrorState title="Tabelkaart niet geladen" message={`${fqn}: ${error}`} onRetry={() => setRetry((r) => r + 1)} />
      </div>
    );
  if (!card) return null;

  return (
    <div>
      {back}
      <CardHeaderBlock
        card={card}
        fqn={fqn}
        onSection={onSection}
        actions={
          <>
            <CopyButton text={typeof window !== "undefined" ? window.location.href : ""} label="Kopieer link" className="hidden h-9 px-3 text-sm sm:inline-flex" />
            <Button variant="outline" size="sm" className="h-9" onClick={() => onAsk(fqn)}>
              <Bot className="h-4 w-4" />
              <span className="sm:hidden">Vraag</span>
              <span className="hidden sm:inline">Vraag de assistent</span>
            </Button>
          </>
        }
      />
      <div ref={sentinel} aria-hidden />
      <div className="sticky top-0 z-10 flex h-11 items-center gap-4 border-b border-border bg-card/95 px-5 backdrop-blur lg:px-8">
        {stuck && (
          <span className="hidden max-w-[16rem] shrink-0 truncate font-mono text-sm font-semibold xl:block" title={fqn}>
            {name}
          </span>
        )}
        <nav
          className="-mb-px flex h-full min-w-0 flex-1 gap-1 overflow-x-auto [mask-image:linear-gradient(to_right,black_85%,transparent)] sm:[mask-image:none]"
          role="tablist"
          aria-label="Onderdelen van de kaart"
        >
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={active === s.id}
              onClick={() => onSection(s.id)}
              className={cn(
                "inline-flex h-full shrink-0 items-center gap-1.5 border-b-2 px-3 text-sm font-medium transition-colors",
                active === s.id ? "border-sl-charcoal text-foreground" : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {s.label}
              {s.count !== undefined && <span className={cn("rounded-full px-1.5 text-xs tabular-nums", active === s.id ? "bg-sl-charcoal text-white" : "bg-muted")}>{s.count}</span>}
            </button>
          ))}
        </nav>
      </div>
      <div className="px-5 py-6 lg:px-8" role="tabpanel">
        <CardSectionBody card={card} section={active} onOpenTable={onOpenTable} onSection={onSection} />
      </div>
    </div>
  );
}

function ConceptDetail({
  concept,
  index,
  onOpenTable,
  onBack,
}: {
  concept: Concept;
  index: Map<string, TableSummary>;
  onOpenTable: (fqn: string) => void;
  onBack: () => void;
}) {
  const { name, definition, authoritative_source, related_tables, ...rest } = concept as Concept & Record<string, unknown>;
  const src = typeof authoritative_source === "string" ? authoritative_source : "";
  const srcTable = src.match(/^([\w-]+\.)?[\w-]+\.[\w-]+/)?.[0];
  const extra = Object.entries(rest).filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <div>
      <button type="button" onClick={onBack} className="inline-flex h-10 items-center gap-1.5 px-4 text-sm font-medium text-muted-foreground hover:text-foreground lg:hidden">
        <ArrowLeft className="h-4 w-4" />
        Alle begrippen
      </button>
      <div className="px-5 pb-4 pt-5 lg:px-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BookMarked className="h-4 w-4" />
          Begrip
          {extra.map(([k, v]) => (
            <span key={k}>· {String(v)}</span>
          ))}
        </div>
        <h2 className="mt-1 text-2xl font-semibold leading-tight">{name}</h2>
      </div>
      <div className="space-y-8 border-t border-border px-5 py-6 lg:px-8">
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Definitie</h3>
          {definition ? <InlineText text={definition} className="block max-w-[75ch] text-[15px] leading-relaxed" /> : <p className="text-muted-foreground">Geen definitie vastgelegd.</p>}
        </section>
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gezaghebbende bron</h3>
          {src ? (
            <p className="text-sm">
              {srcTable ? (
                <button type="button" onClick={() => onOpenTable(shortFqn(srcTable))} className="font-mono font-medium text-info underline-offset-2 hover:underline">
                  {srcTable}
                </button>
              ) : null}
              <span className="font-mono text-muted-foreground">{srcTable ? src.slice(srcTable.length) : src}</span>
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">Niet vastgelegd.</p>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Gerelateerde tabellen ({related_tables?.length ?? 0})</h3>
          {related_tables?.length ? (
            <ul className="divide-y divide-border rounded-md border border-border">
              {related_tables.map((t) => {
                const s = index.get(shortFqn(t));
                return (
                  <li key={t}>
                    <button type="button" onClick={() => onOpenTable(shortFqn(t))} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-sl-grey-50">
                      <Table2 className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="font-mono text-sm font-semibold">{shortFqn(t).split(".").pop()}</span>
                          <span className="font-mono text-xs text-muted-foreground">{shortFqn(t).split(".")[0]}</span>
                          {s?.kind && <KindBadge kind={s.kind} />}
                        </span>
                        {s?.summary && <span className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{s.summary}</span>}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Geen.</p>
          )}
        </section>
      </div>
    </div>
  );
}

function snippet(text: string, q: string, len = 160): string {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text.length > len ? `${text.slice(0, len)}…` : text;
  const start = Math.max(0, i - 60);
  return `${start > 0 ? "…" : ""}${text.slice(start, start + len)}${start + len < text.length ? "…" : ""}`;
}

function SearchResults({ q, tables, onOpen }: { q: string; tables: TableSummary[]; onOpen: (fqn: string) => void }) {
  const f = q.toLowerCase();
  const byName = tables.filter((t) => t.fqn.split(".").pop()!.toLowerCase().includes(f));
  const rest = tables.filter((t) => !byName.includes(t));
  const Row = ({ t }: { t: TableSummary }) => {
    const [ds, ...r] = t.fqn.split(".");
    const inSummary = !!t.summary && t.summary.toLowerCase().includes(f);
    return (
      <li>
        <button type="button" onClick={() => onOpen(t.fqn)} className="block w-full px-4 py-3 text-left hover:bg-sl-grey-50">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-semibold">
              <Hl text={r.join(".") || t.fqn} q={q} />
            </span>
            <span className="font-mono text-xs text-muted-foreground">{ds}</span>
            <KindBadge kind={t.kind} />
          </span>
          {t.summary && (
            <span className="mt-1 block text-[13px] leading-relaxed text-muted-foreground">
              <Hl text={snippet(t.summary, q)} q={q} />
            </span>
          )}
          {!inSummary && !r.join(".").toLowerCase().includes(f) && <span className="mt-1 block text-xs italic text-muted-foreground">Treffer in kolommen, synoniemen of andere kaartvelden</span>}
        </button>
      </li>
    );
  };
  return (
    <div className="mx-auto max-w-4xl px-6 py-6 lg:px-10">
      <h2 className="text-lg font-semibold">
        {tables.length} resultaten voor “{q}”
      </h2>
      {byName.length > 0 && (
        <section className="mt-5">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Naam komt overeen ({byName.length})</h3>
          <ul className="divide-y divide-border rounded-md border border-border">
            {byName.slice(0, 30).map((t) => (
              <Row key={t.fqn} t={t} />
            ))}
          </ul>
        </section>
      )}
      {rest.length > 0 && (
        <section className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Treffer in de inhoud ({rest.length})</h3>
          <ul className="divide-y divide-border rounded-md border border-border">
            {rest.slice(0, 30).map((t) => (
              <Row key={t.fqn} t={t} />
            ))}
          </ul>
          {rest.length > 30 && <p className="mt-2 text-sm text-muted-foreground">Verfijn je zoekterm om de overige {rest.length - 30} te zien, of blader in de lijst links.</p>}
        </section>
      )}
    </div>
  );
}

const selKeyForList = (s: BrowserSelection) => (s ? (s.type === "table" ? s.fqn : s.name) : "");

const RECENT_KEY = "databasecontext.recent";
function rememberRecent(fqn: string) {
  try {
    const cur: string[] = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    localStorage.setItem(RECENT_KEY, JSON.stringify([fqn, ...cur.filter((x) => x !== fqn)].slice(0, 8)));
  } catch {
    /* storage unavailable */
  }
}
function readRecent(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function CatalogHome({
  version,
  tables,
  nConcepts,
  onDomain,
  onOpen,
  query,
  nResults,
}: {
  version?: Version;
  tables: TableSummary[] | null;
  nConcepts?: number;
  onDomain: (d: string) => void;
  onOpen: (fqn: string) => void;
  query?: string;
  nResults?: number;
}) {
  const recent = useMemo(() => readRecent(), []);
  const index = useMemo(() => new Map((tables ?? []).map((t) => [t.fqn, t])), [tables]);
  const byDomain = useMemo(() => {
    const m = new Map<string, { n: number; fact: number; dim: number }>();
    (tables ?? []).forEach((t) => {
      const d = t.domain || "overig";
      const e = m.get(d) ?? { n: 0, fact: 0, dim: 0 };
      e.n++;
      if (t.kind === "fact") e.fact++;
      if (t.kind === "dim") e.dim++;
      m.set(d, e);
    });
    return [...m.entries()].sort((a, b) => b[1].n - a[1].n);
  }, [tables]);
  return (
    <div className="mx-auto max-w-4xl px-6 py-10 lg:px-10">
      <h2 className="text-xl font-semibold">Kies een tabel of begrip</h2>
      <p className="mt-1 max-w-[70ch] text-[15px] leading-relaxed text-muted-foreground">
        Elke tabelkaart bevat wat de agents weten: doel, granulariteit, kolommen met betekenis, joins, valkuilen en geverifieerde SQL-recepten. Zoek links op naam,
        kolom of betekenis. Tip: <kbd className="rounded border border-border bg-card px-1.5 font-mono text-xs">/</kbd> springt naar de zoekbalk, pijltjestoetsen
        bladeren door de lijst.
      </p>
      {query ? (
        <p className="mt-4 rounded-md border border-border bg-sl-grey-50 px-4 py-3 text-sm">
          <span className="font-semibold tabular-nums">{nResults ?? 0}</span> resultaten voor “{query}”. Tabellen waarvan de naam overeenkomt staan bovenaan; “treffer in inhoud”
          betekent dat de term in een kolom, synoniem of beschrijving voorkomt.
        </p>
      ) : (
        version && (
          <p className="mt-4 text-sm text-muted-foreground">
            Deze versie bevat <span className="font-medium text-foreground">{tables?.length ?? version.n_tables} tabellen</span> en{" "}
            <span className="font-medium text-foreground">{nConcepts ?? version.n_concepts} begrippen</span>.
          </p>
        )
      )}
      {recent.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Recent bekeken</h3>
          <ul className="divide-y divide-border rounded-md border border-border bg-card">
            {recent.map((f) => {
              const t = index.get(f);
              return (
                <li key={f}>
                  <button type="button" onClick={() => onOpen(f)} className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-sl-grey-50">
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="truncate font-mono text-sm font-semibold">{f.split(".").pop()}</span>
                        <span className="truncate font-mono text-xs text-muted-foreground">{f.split(".")[0]}</span>
                      </span>
                      {t?.summary && <span className="mt-0.5 line-clamp-1 text-[13px] text-muted-foreground">{t.summary}</span>}
                    </span>
                    {t?.kind && <KindBadge kind={t.kind} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {byDomain.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Per domein</h3>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {byDomain.map(([d, e]) => (
              <button
                key={d}
                type="button"
                onClick={() => onDomain(d === "overig" ? "" : d)}
                className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-2.5 text-left transition-colors hover:border-sl-grey-300 hover:bg-sl-grey-50"
              >
                <span className="min-w-0 truncate font-mono text-sm">{d}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                  {e.n} · {e.fact} feit · {e.dim} dim
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function CatalogView({
  version,
  versionInfo,
  tab,
  onTab,
  selection,
  onSelect,
  section,
  onSection,
  onAsk,
  railCollapsed,
}: {
  /** Collapse the list rail (e.g. while the assistant is docked on a medium-wide screen). */
  railCollapsed?: boolean;
  version?: string;
  versionInfo?: Version;
  tab: CatalogTab;
  onTab: (t: CatalogTab) => void;
  selection: BrowserSelection;
  onSelect: (s: BrowserSelection) => void;
  section: CardSection;
  onSection: (s: CardSection) => void;
  onAsk: (fqn: string) => void;
}) {
  const [search, setSearch] = useState("");
  const [domain, setDomain] = useState("");
  const [kind, setKind] = useState("");
  const q = useDebounced(search.trim(), 250);
  const tables = useTables(version, q, domain);
  const concepts = useConcepts(version, tab === "begrippen" || !selection || selection.type === "concept");
  const searchRef = useRef<HTMLInputElement>(null);
  const detailRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [userCollapsed, setUserCollapsed] = useState(() => {
    try {
      return localStorage.getItem("databasecontext.rail") === "dicht";
    } catch {
      return false;
    }
  });
  const collapsed = !!selection && (userCollapsed || !!railCollapsed);
  const [railW, setRailW] = useState(() => {
    const v = Number(typeof localStorage !== "undefined" ? localStorage.getItem("databasecontext.railw") : NaN);
    return Number.isFinite(v) && v >= 240 && v <= 440 ? v : 300;
  });
  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    const x0 = e.clientX;
    const w0 = railW;
    let w = w0;
    const move = (ev: PointerEvent) => {
      w = Math.min(440, Math.max(240, w0 + ev.clientX - x0));
      setRailW(w);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      try {
        localStorage.setItem("databasecontext.railw", String(w));
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };
  const setCollapsed = (v: boolean) => {
    setUserCollapsed(v);
    try {
      localStorage.setItem("databasecontext.rail", v ? "dicht" : "open");
    } catch {
      /* ignore */
    }
  };

  // "/" focuses search.
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === "/" && !["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Keep the open item visible in the list.
  useEffect(() => {
    const el = listRef.current?.querySelector('[aria-current="true"]') as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, [selKeyForList(selection), tables.data, collapsed]);

  // Scroll detail to top when the selected item changes.
  const selKey = selection ? (selection.type === "table" ? `t:${selection.fqn}` : `c:${selection.name}`) : "";
  useEffect(() => {
    detailRef.current?.scrollTo({ top: 0 });
  }, [selKey]);
  useEffect(() => {
    if (detailRef.current && detailRef.current.scrollTop > 300) {
      const tabs = detailRef.current.querySelector('[role="tablist"]');
      (tabs?.parentElement as HTMLElement | undefined)?.scrollIntoView({ block: "start" });
    }
  }, [section]);

  // Summaries by fqn (merged across searches) for concept detail.
  const [index, setIndex] = useState<Map<string, TableSummary>>(new Map());
  const [domains, setDomains] = useState<string[]>([]);
  useEffect(() => {
    if (!tables.data) return;
    setIndex((prev) => {
      const m = new Map(prev);
      tables.data!.forEach((t) => m.set(t.fqn, t));
      return m;
    });
    if (!domain) {
      const ds = [...new Set(tables.data.map((t) => t.domain).filter((d): d is string => !!d))].sort();
      setDomains((prev) => (q ? [...new Set([...prev, ...ds])].sort() : ds));
    }
  }, [tables.data, domain, q]);
  const [allTables, setAllTables] = useState<TableSummary[] | null>(null);
  useEffect(() => {
    if (!q && !domain && tables.data) setAllTables(tables.data);
  }, [tables.data, q, domain]);

  const shownTables = useMemo(() => {
    let list = tables.data ?? [];
    if (kind) list = list.filter((t) => (t.kind || "other") === kind);
    if (q) {
      const f = q.toLowerCase();
      // Exact name, then a name segment (dim_klanten for "klant"), then substring, then content matches;
      // canonical dims/facts before reporting models within a rank.
      const score = (t: TableSummary) => {
        const n = t.fqn.split(".").pop()!.toLowerCase();
        const segs = n.split(/[_-]/);
        const base = n === f ? 0 : segs.some((x) => x === f || x.startsWith(f)) ? 1 : n.includes(f) ? 2 : 3;
        return base * 10 + (t.kind === "dim" ? 0 : t.kind === "fact" ? 1 : 2) + (t.fqn.includes("_combined.") ? 0 : 0.5);
      };
      list = [...list].sort((a, b) => score(a) - score(b) || a.fqn.localeCompare(b.fqn));
    }
    return list;
  }, [tables.data, kind, q]);

  const shownConcepts = useMemo(() => {
    const f = q.toLowerCase();
    const list = concepts.data ?? [];
    if (!f) return list;
    return list.filter((c) => `${c.name} ${c.definition ?? ""} ${(c.related_tables ?? []).join(" ")}`.toLowerCase().includes(f));
  }, [concepts.data, q]);

  const selectedConcept = selection?.type === "concept" ? (concepts.data ?? []).find((c) => c.name === selection.name) : undefined;
  const filtered = !!(q || domain || kind);
  const openTable = (fqn: string) => onSelect({ type: "table", fqn });

  const list: ReactNode =
    tab === "tabellen" ? (
      tables.error ? (
        <div className="p-4">
          <Alert tone="danger">
            Tabellen niet geladen: {tables.error}{" "}
            <button type="button" className="font-medium underline" onClick={tables.retry}>
              Opnieuw
            </button>
          </Alert>
        </div>
      ) : tables.loading && !tables.data ? (
        <ListSkeleton />
      ) : !shownTables.length ? (
        <EmptyState
          icon={<Table2 className="h-5 w-5" />}
          title={filtered ? "Geen tabellen gevonden" : "Nog geen tabellen"}
          description={filtered ? `Niets voor “${search || domain || kindLabel(kind)}”. Zoek op een deel van de naam, een kolom of een begrip.` : "Deze versie bevat nog geen tabelkaarten."}
          action={
            filtered ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearch("");
                  setDomain("");
                  setKind("");
                }}
              >
                Filters wissen
              </Button>
            ) : undefined
          }
        />
      ) : (
        <ul className={cn("divide-y divide-border", tables.loading && "opacity-60")} onKeyDown={onListKey} aria-label="Tabellen">
          {shownTables.map((t) => {
            const active = selection?.type === "table" && selection.fqn === t.fqn;
            const [ds, ...rest] = t.fqn.split(".");
            const name = rest.join(".") || t.fqn;
            const nameMatch = !q || name.toLowerCase().includes(q.toLowerCase());
            return (
              <li key={t.fqn}>
                <button
                  type="button"
                  data-item
                  onClick={() => openTable(t.fqn)}
                  className={cn(
                    "block w-full px-4 py-2.5 text-left transition-colors hover:bg-sl-grey-50 focus-visible:bg-sl-grey-50",
                    active && "bg-accent shadow-[inset_3px_0_0_var(--color-sl-yellow)] hover:bg-accent",
                  )}
                  aria-current={active || undefined}
                  title={t.fqn}
                >
                  <span className="flex items-center gap-2">
                    <span className="min-w-0 flex-1 truncate font-mono text-[13px] font-semibold text-foreground">
                      <Hl text={name} q={q} />
                    </span>
                    <KindBadge kind={t.kind} />
                  </span>
                  <span className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="truncate font-mono">{ds}</span>
                    {!nameMatch && <span className="shrink-0 rounded bg-muted px-1.5 text-[11px]">treffer in inhoud</span>}
                  </span>
                  {t.summary && <span className="mt-1 line-clamp-1 text-xs text-muted-foreground">{t.summary}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )
    ) : concepts.error ? (
      <div className="p-4">
        <Alert tone="danger">
          Begrippen niet geladen: {concepts.error}{" "}
          <button type="button" className="font-medium underline" onClick={concepts.retry}>
            Opnieuw
          </button>
        </Alert>
      </div>
    ) : concepts.loading && !concepts.data ? (
      <ListSkeleton />
    ) : !shownConcepts.length ? (
      <EmptyState
        icon={<BookMarked className="h-5 w-5" />}
        title={q ? "Geen begrippen gevonden" : "Nog geen begrippen"}
        description={q ? "Pas je zoekterm aan." : "Deze versie bevat nog geen begrippen."}
      />
    ) : (
      <ul className="divide-y divide-border" onKeyDown={onListKey} aria-label="Begrippen">
        {shownConcepts.map((c) => {
          const active = selection?.type === "concept" && selection.name === c.name;
          return (
            <li key={c.name}>
              <button
                type="button"
                data-item
                onClick={() => onSelect({ type: "concept", name: c.name })}
                className={cn(
                  "block w-full px-4 py-2.5 text-left transition-colors hover:bg-sl-grey-50 focus-visible:bg-sl-grey-50",
                  active && "bg-accent shadow-[inset_3px_0_0_var(--color-sl-yellow)] hover:bg-accent",
                )}
                aria-current={active || undefined}
              >
                <span className="flex items-center gap-2">
                  <span className="min-w-0 flex-1 truncate text-sm font-semibold">
                    <Hl text={c.name} q={q} />
                  </span>
                  {!!c.related_tables?.length && <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{c.related_tables.length} tab.</span>}
                </span>
                {c.definition && <span className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{c.definition}</span>}
              </button>
            </li>
          );
        })}
      </ul>
    );

  const total = tab === "tabellen" ? (versionInfo?.n_tables ?? allTables?.length) : (concepts.data?.length ?? versionInfo?.n_concepts);
  const nShown = tab === "tabellen" ? shownTables.length : shownConcepts.length;

  return (
    <div className="flex min-h-0 flex-1">
      {/* Rail */}
      {collapsed && (
        <div className="hidden w-11 shrink-0 flex-col items-center gap-2 border-r border-border bg-card py-3 lg:flex">
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(false)} aria-label="Lijst tonen" title="Lijst tonen">
            <PanelLeftOpen className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              setCollapsed(false);
              setTimeout(() => searchRef.current?.focus(), 0);
            }}
            aria-label="Zoeken"
            title="Zoeken (/)"
          >
            <Search className="h-4 w-4" />
          </Button>
        </div>
      )}
      <aside
        className={cn("relative flex min-h-0 w-full shrink-0 flex-col border-r border-border bg-card lg:w-[var(--rail-w)]", selection && "hidden lg:flex", collapsed && "lg:hidden")}
        style={{ ["--rail-w" as string]: `${railW}px` }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Breedte van de lijst aanpassen"
          aria-valuenow={railW}
          aria-valuemin={240}
          aria-valuemax={440}
          tabIndex={0}
          onPointerDown={startResize}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
              e.preventDefault();
              const w = Math.min(440, Math.max(240, railW + (e.key === "ArrowRight" ? 20 : -20)));
              setRailW(w);
              try {
                localStorage.setItem("databasecontext.railw", String(w));
              } catch {
                /* ignore */
              }
            }
          }}
          className="absolute inset-y-0 -right-1 z-20 hidden w-2 cursor-col-resize hover:bg-sl-yellow/40 focus-visible:bg-sl-yellow/60 lg:block"
        />
        <div className="space-y-2.5 border-b border-border p-3">
          <div className="flex items-center gap-1.5">
          <div className="grid flex-1 grid-cols-2 rounded-md bg-muted p-0.5" role="tablist" aria-label="Soort">
            {(["tabellen", "begrippen"] as const).map((t) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => onTab(t)}
                className={cn(
                  "h-8 rounded-[5px] text-sm font-medium transition-colors",
                  tab === t ? "bg-card text-foreground shadow-card" : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t === "tabellen" ? "Tabellen" : "Begrippen"}
              </button>
            ))}
          </div>
            {selection && (
              <Button variant="ghost" size="icon" className="hidden h-9 w-9 lg:inline-flex" onClick={() => setCollapsed(true)} aria-label="Lijst inklappen" title="Lijst inklappen">
                <PanelLeftClose className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              type="text"
              role="searchbox"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tab === "tabellen" ? "Zoek tabel, kolom of betekenis" : "Zoek begrippen"}
              className="h-9 pl-8 pr-8"
              aria-label={tab === "tabellen" ? "Zoek tabellen" : "Zoek begrippen"}
            />
            {search ? (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label="Zoekterm wissen"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            ) : (
              <kbd className="pointer-events-none absolute right-2 top-1/2 hidden -translate-y-1/2 rounded border border-border px-1.5 font-mono text-[11px] text-muted-foreground lg:block">/</kbd>
            )}
          </div>
          {tab === "tabellen" && (
            <div className="grid grid-cols-2 gap-2">
              <Select value={domain} onChange={(e) => setDomain(e.target.value)} className="h-8 text-xs" aria-label="Domein">
                <option value="">Alle domeinen</option>
                {domains.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
              <Select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 text-xs" aria-label="Tabeltype">
                <option value="">Alle typen</option>
                <option value="fact">Feit</option>
                <option value="dim">Dimensie</option>
                <option value="other">Overig</option>
              </Select>
            </div>
          )}
          <p className="px-0.5 text-xs tabular-nums text-muted-foreground" aria-live="polite">
            {filtered || tab === "begrippen" ? `${nShown} van ${total ?? "?"} ${tab}` : `${nShown} ${tab}`}
          </p>
        </div>
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto">
          {selection?.type === "table" && tab === "tabellen" && tables.data && !shownTables.some((t) => t.fqn === selection.fqn) && (
            <div className="border-b border-border bg-accent px-4 py-2 text-xs">
              <span className="text-muted-foreground">Geopend, niet in deze lijst: </span>
              <span className="font-mono font-semibold">{selection.fqn.split(".").pop()}</span>
            </div>
          )}
          {list}
        </div>
      </aside>

      {/* Detail */}
      <div ref={detailRef} data-pane="detail" className={cn("min-h-0 min-w-0 flex-1 overflow-y-auto bg-card", !selection && "hidden lg:block")}>
        {selection?.type === "table" ? (
          <TableDetail
            key={`${selection.fqn}@${version ?? ""}`}
            fqn={selection.fqn}
            version={version}
            section={section}
            onSection={onSection}
            onOpenTable={openTable}
            onAsk={onAsk}
            onBack={() => onSelect(null)}
            onSearch={(term) => {
              onTab("tabellen");
              setSearch(term);
              setCollapsed(false);
              onSelect(null);
            }}
          />
        ) : selection?.type === "concept" ? (
          selectedConcept ? (
            <ConceptDetail concept={selectedConcept} index={index} onOpenTable={openTable} onBack={() => onSelect(null)} />
          ) : concepts.loading || !concepts.data ? (
            <DetailSkeleton />
          ) : (
            <EmptyState title="Begrip niet gevonden" description="Dit begrip bestaat niet in de gekozen versie." />
          )
        ) : q && tab === "tabellen" && shownTables.length > 0 ? (
          <SearchResults q={q} tables={shownTables} onOpen={openTable} />
        ) : (
          <CatalogHome
            version={versionInfo}
            tables={allTables}
            nConcepts={concepts.data?.length}
            onOpen={openTable}
            query={q || undefined}
            nResults={tab === "tabellen" ? shownTables.length : shownConcepts.length}
            onDomain={(d) => {
              onTab("tabellen");
              setDomain(d);
            }}
          />
        )}
      </div>
    </div>
  );
}

